import type { CardStat, MyBrandCreativeCardModel } from "./drilldown-card.types"
import { asDrilldownDotColor, resolveDupBadgeCount } from "./drilldown-card-format"
import { accountKpiKey, resolveCardKpiTrend } from "./drilldown-card-trend"
import type { MyBrandKpiCatalogEntry } from "./my-brand-kpi-catalog"
import { formatTableValue } from "./format-table-value"
import { buildAdLibUrl, daysBetweenYmd, extractNumber, extractString } from "./drilldown-row"

/**
 * Maps a raw My Brand BQ row into the redesigned card's view model. `kpiIds` is
 * the user's ordered `myBrandKpis` selection — index 0 renders "big" on the
 * card, the rest render "compact" (positional, per the product decision;
 * independent of which KPI is the active sort).
 */
export function mapMyBrandCardRow(
  row: Record<string, unknown>,
  kpiIds: string[],
  catalogById: Record<string, MyBrandKpiCatalogEntry> = {},
  index = 0,
  currencySymbol = "$",
): MyBrandCreativeCardModel {
  const hash = extractString(row.hash)
  const adId = extractString(row.ad_id)
  const resolvedAdId = adId && adId !== "Multiple" ? adId : null
  const objectType = extractString(row.object_type)
  const minDate = extractString(row.min_date)
  const maxDate = extractString(row.max_date)
  const creatives = extractNumber(row.creatives) ?? 0

  const kpis: CardStat[] = kpiIds.map((id) => {
    const entry = catalogById[id]
    const value = extractNumber(row[id])
    const trend = resolveCardKpiTrend(id, value, extractNumber(row[accountKpiKey(id)]), {
      polarity: entry?.polarity,
      hasSumFormula: entry?.hasSumFormula,
    })
    return {
      label: entry?.label ?? id,
      value: formatTableValue(value, entry?.format ?? "number", currencySymbol, entry?.decimals ?? 2),
      dotColor: asDrilldownDotColor(entry?.colorVar ?? ""),
      ...(trend ?? {}),
    }
  })

  return {
    // Falls back to a row-index-derived key (never ""), same as DrilldownListView's
    // defaultRowKey — rows missing both identifiers must not collide as React/virtualizer keys.
    id: hash || adId || `row-${index}`,
    hash,
    // My Brand never returns carousel object types (confirmed: no included_hashes/
    // _types on DrilldownMyBrandRow) — any non-"image" type plays as a single video.
    isVideo: objectType.toLowerCase() !== "image",
    objectTypeLabel: objectType,
    thumbnailUrl: extractString(row.thumbnail_url),
    assetUrl: extractString(row.creative_url),
    adLibUrl: buildAdLibUrl(adId),
    dupCount: resolveDupBadgeCount(creatives),
    daysActive: daysBetweenYmd(minDate, maxDate),
    minDate,
    maxDate,
    kpis,
    adId: resolvedAdId,
  }
}
